import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { fetchPublicMenu } from "../../services/ApiCalls";
import { useAppContext } from "../../context/AppContext";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getCookie, setCookie, removeCookie } from "../../util/cookies";
import { opmStorage } from "../../util/opmStorage";
import { ForwardIcon } from "../../Components/Icons";
import { ChefFigure } from "../../Components/Chef";

const INK = "#1F1A1A";
// splashChef1a/1b … splashChef4a/4b in the locales.
const CHEF_LINES = 4;
const STEAM = [
  { left: 84, top: 250, size: 48, delay: "1.4s" },
  { left: 286, top: 244, size: 48, delay: "2.2s" },
  { left: 252, top: 262, size: 40, delay: "3s" },
];
// Around the plate, drifting: a leaf, a lemon slice, a tomato and a pepper.
const INGREDIENTS = [
  {
    pos: { left: 22, top: 232 },
    enter: "1.5s",
    drift: "0s",
    svg: (
      <svg width="40" height="40" viewBox="0 0 40 40">
        <path d="M6 34 C6 16 18 6 34 6 C34 22 24 34 6 34 Z" fill="#8CC56B" stroke={INK} strokeWidth="2.5" strokeLinejoin="round" />
        <path d="M6 34 L24 16" stroke={INK} strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    pos: { right: 22, top: 250 },
    enter: "1.7s",
    drift: ".8s",
    svg: (
      <svg width="38" height="38" viewBox="0 0 40 40">
        <circle cx="20" cy="20" r="16" fill="#FFD54F" stroke={INK} strokeWidth="2.5" />
        <circle cx="20" cy="20" r="11" fill="#FFF3B0" />
        <path d="M20 9 V31 M9 20 H31 M12 12 L28 28 M28 12 L12 28" stroke="#FFD54F" strokeWidth="1.6" />
      </svg>
    ),
  },
  {
    pos: { left: 54, top: 392 },
    enter: "1.9s",
    drift: "1.4s",
    svg: (
      <svg width="34" height="34" viewBox="0 0 40 40">
        <circle cx="20" cy="20" r="15" fill="#FF6B57" stroke={INK} strokeWidth="2.5" />
        <circle cx="15" cy="17" r="2.2" fill="#FFE1A8" />
        <circle cx="24" cy="15" r="2.2" fill="#FFE1A8" />
        <circle cx="22" cy="25" r="2.2" fill="#FFE1A8" />
      </svg>
    ),
  },
  {
    pos: { right: 60, top: 386 },
    enter: "2.1s",
    drift: ".4s",
    svg: (
      <svg width="44" height="30" viewBox="0 0 44 30">
        <path d="M6 22 C14 26 30 24 38 10 C40 6 36 4 34 8 C28 18 16 20 8 16 C4 15 3 20 6 22 Z" fill="#3E9B4F" stroke={INK} strokeWidth="2.5" strokeLinejoin="round" />
        <path d="M36 8 L40 3" stroke={INK} strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    ),
  },
];

const applyMenuPayload = (response) => {
  const menus = response?.menus || [];
  const active = menus.find((m) => m.is_active) || null;
  // The restaurant's name comes beside the menus; keep it with the menu so it is cached with it.
  return active ? { ...active, restaurant_name: response?.restaurant_name || "" } : null;
};

const Index = () => {
  const { t } = useTranslation();
  // Set once the menu loaded and the guest came from the QR code: wait for their tap.
  const [readyToEnter, setReadyToEnter] = useState(null);
  const {
    setMenu,
    setIsSplashScreen,
    setIsAuthenticated,
    setAuthClaims,
    setAuthToken,
    setGuestRestaurantId,
    setGuestBrowseReady,
    restaurantName,
  } = useAppContext();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const hasValidated = useRef(false);

  useEffect(() => {
    if (hasValidated.current) return;
    hasValidated.current = true;

    setIsSplashScreen(true);

    const urlRestaurant = searchParams.get("restaurant")?.trim();
    const cookieRestaurantId = getCookie("opm_restaurant_id")?.trim();

    if (
      urlRestaurant &&
      cookieRestaurantId &&
      urlRestaurant !== cookieRestaurantId
    ) {
      opmStorage.clearRestaurant(cookieRestaurantId);
      removeCookie("opm_element_id");
    }

    const fail = (reason) => {
      setGuestBrowseReady(false);
      setTimeout(() => {
        setIsSplashScreen(false);
        navigate(`/error?reason=${reason}`, { replace: true });
      }, 1200);
    };

    // From the QR code the guest sees the welcome screen and taps in; on a reload they
    // continue straight to where they were.
    const finishOk = (stripQuery) => {
      if (stripQuery) {
        setReadyToEnter(() => () => {
          setIsSplashScreen(false);
          navigate("/", { replace: true });
        });
      } else {
        setIsSplashScreen(false);
      }
    };

    const runPublicMenu = async (restaurantId, stripQuery) => {
      setGuestBrowseReady(false);
      const response = await fetchPublicMenu(restaurantId);
      const activeMenu = applyMenuPayload(response);
      setMenu(activeMenu);
      setGuestRestaurantId(restaurantId);
      setGuestBrowseReady(true);
      setCookie("opm_restaurant_id", restaurantId);
      setIsAuthenticated(false);
      setAuthToken(null);
      setAuthClaims(null);
      finishOk(stripQuery);
    };

    const start = async () => {
      try {
        if (urlRestaurant) {
          await runPublicMenu(urlRestaurant, true);
          return;
        }

        if (cookieRestaurantId) {
          await runPublicMenu(cookieRestaurantId, false);
          return;
        }

        fail("missing_restaurant");
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404) fail("menu_not_found");
        else fail("menu_load_failed");
      }
    };

    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The chef's speech bubble: each tap on him moves to the next line.
  const [chefLine, setChefLine] = useState(0);
  const [chefHop, setChefHop] = useState(false);
  const hopTimer = useRef(null);
  useEffect(() => () => clearTimeout(hopTimer.current), []);
  const pokeChef = () => {
    clearTimeout(hopTimer.current);
    setChefHop(true);
    setChefLine((line) => (line + 1) % CHEF_LINES);
    hopTimer.current = setTimeout(() => setChefHop(false), 560);
  };

  // Coming back from the payment page the guest has already seen the welcome screen: keep the
  // menu loading underneath and show the same waiting page the return screen uses, so the
  // switch between the two is invisible.
  if (pathname.startsWith("/payment-return")) {
    return (
      <div className="h-dvh flex items-center justify-center bg-background" aria-busy="true">
        <span
          className="w-10 h-10 rounded-full border-4 border-primary-50 border-t-primary animate-spin"
          role="img"
          aria-label={t("confirmingPayment")}
        />
      </div>
    );
  }

  return (
    <div className="relative h-dvh w-full overflow-hidden bg-primary text-white flex flex-col items-center">
      <div aria-hidden="true" className="absolute -left-[70px] top-[150px] w-[230px] h-[230px] rounded-full bg-white opacity-[0.07]" />
      <div aria-hidden="true" className="absolute -right-[90px] top-[520px] w-[280px] h-[280px] rounded-full bg-white opacity-[0.07]" />
      <div aria-hidden="true" className="absolute left-[250px] -top-[70px] w-[170px] h-[170px] rounded-full bg-white opacity-[0.07]" />

      <div className="splash-title relative z-10 pt-[66px] px-6 flex flex-col items-center gap-1 text-center">
        <h1 className="font-display font-bold text-[70px] leading-[72px] min-h-[72px]">{restaurantName}</h1>
        <p className="text-base font-medium max-w-[300px] text-balance">{t("SplashScreenDescription")}</p>
      </div>

      <div className="relative z-10 flex-1 min-h-0 w-full flex items-center justify-center">
        <div className="splash-stage relative flex-none w-[390px] h-[400px]">
          <div className="splash-plate absolute left-[35px] top-[290px]">
            <svg width="320" height="80" viewBox="0 0 320 80" aria-hidden="true">
              <ellipse cx="160" cy="46" rx="156" ry="34" fill="#000000" opacity="0.14" />
              <ellipse cx="160" cy="40" rx="155" ry="34" fill="#FFFFFF" stroke={INK} strokeWidth="3" />
              <ellipse cx="160" cy="40" rx="122" ry="24" fill="#F7EDEE" stroke={INK} strokeWidth="2" />
              <ellipse cx="160" cy="40" rx="74" ry="12" fill={INK} opacity="0.10" />
            </svg>
          </div>

          {STEAM.map(({ left, top, size, delay }) => (
            <div key={delay} className="splash-steam absolute" style={{ left, top, animationDelay: delay }} aria-hidden="true">
              <svg width={size * 22 / 48} height={size} viewBox="0 0 22 48">
                <path d="M11 46 C2 36 20 30 11 20 C2 10 20 6 11 2" fill="none" stroke="#FFFFFF" strokeWidth="3.5" strokeLinecap="round" />
              </svg>
            </div>
          ))}

          <div className="absolute left-[110px] top-[60px] w-[170px] h-[270px] overflow-hidden">
            <div className="splash-rise absolute left-0 bottom-0 w-[170px] h-[213px]">
              <div className="splash-bob">
                <button
                  type="button"
                  onClick={pokeChef}
                  aria-label={t("splashChefPoke")}
                  className={`block p-0 border-0 bg-transparent cursor-pointer rounded-3xl focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-white focus-visible:outline-offset-4 ${chefHop ? "splash-hop" : ""}`}
                >
                  <ChefFigure width={170} height={213} tie="#FFC93C" />
                </button>
              </div>
            </div>
          </div>

          <svg className="splash-plate absolute left-[35px] top-[290px] pointer-events-none" width="320" height="80" viewBox="0 0 320 80" aria-hidden="true">
            <path d="M5 40 A155 34 0 0 0 315 40 L282 40 A122 24 0 0 1 38 40 Z" fill="#FFFFFF" stroke={INK} strokeWidth="2.5" strokeLinejoin="round" />
          </svg>

          <div className="splash-cloche absolute left-[75px] top-[178px] pointer-events-none" aria-hidden="true">
            <svg width="240" height="154" viewBox="0 0 240 154">
              <rect x="112" y="14" width="16" height="16" rx="4" fill="#FFFFFF" stroke={INK} strokeWidth="3" />
              <circle cx="120" cy="12" r="10" fill="#FFFFFF" stroke={INK} strokeWidth="3" />
              <path d="M12 140 A108 108 0 0 1 228 140 Z" fill="#FFFFFF" stroke={INK} strokeWidth="3" strokeLinejoin="round" />
              <path d="M52 104 A74 74 0 0 1 98 50" fill="none" stroke="#F3E4E6" strokeWidth="10" strokeLinecap="round" />
              <rect x="4" y="136" width="232" height="14" rx="7" fill="#FFFFFF" stroke={INK} strokeWidth="3" />
            </svg>
          </div>

          <div
            className="splash-bubble absolute right-[10px] top-[92px] max-w-[132px] px-3.5 py-2.5 rounded-[18px_18px_18px_6px] bg-white text-dark shadow-toast flex flex-col gap-0.5"
            aria-live="polite"
          >
            <span className="text-lg leading-[22px] font-bold text-primary">{t(`splashChef${chefLine + 1}a`)}</span>
            <span className="text-sm leading-[19px] font-medium">{t(`splashChef${chefLine + 1}b`)}</span>
          </div>

          {INGREDIENTS.map(({ pos, enter, drift, svg }) => (
            <div key={enter} className="splash-ing absolute" style={{ ...pos, animationDelay: enter }} aria-hidden="true">
              <div className="splash-drift" style={{ animationDelay: drift }}>
                {svg}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="splash-cta relative z-10 pt-3 flex justify-center">
        <button
          type="button"
          onClick={() => readyToEnter && readyToEnter()}
          disabled={!readyToEnter}
          className="splash-cta-btn h-14 px-7 rounded-[18px] bg-white text-primary shadow-toast flex items-center gap-2.5 text-[17px] font-bold whitespace-nowrap disabled:opacity-90"
        >
          {readyToEnter ? (
            <>
              <span>{t("splashStart")}</span>
              <ForwardIcon size={18} />
            </>
          ) : (
            <span className="flex items-center gap-2" role="status">
              <span className="w-4 h-4 rounded-full border-2 border-primary/30 border-t-primary animate-spin" aria-hidden="true" />
              {t("loading")}
            </span>
          )}
        </button>
      </div>

      <div className="splash-foot relative z-10 pt-11 pb-6 flex flex-col items-center gap-[5px]" dir="ltr">
        <span className="text-[11px] leading-[14px] font-medium opacity-85">Powered by</span>
        <img src={`${process.env.PUBLIC_URL}/domingo-logo-white.png`} alt="Domingo · All in One" className="h-8 w-auto block" />
      </div>
    </div>
  );
};
export default Index;
